// SecureEmbed InnovateFlow — Key Hierarchy Tree (Story #1303)
// Visual tree showing org → project → folder → document key hierarchy
// with colour-coded inheritance indicators

import { useState } from 'react'
import { Shield, Key, ChevronRight, ChevronDown, Copy, Check, AlertTriangle } from 'lucide-react'

export interface KeyInfo {
  tenant_id: string
  key_type: string
  kms_key_arn: string
  key_source: 'customer-managed' | 'platform-managed'
  status: string
  created_at: string
  rotated_at?: string
  key_alias?: string
  key_description?: string
  scope_type?: string
  scope_id?: string
  parent_key_id?: string
}

interface KeyHierarchyTreeProps {
  orgKeys: KeyInfo[]
  projectKeys: KeyInfo[]
  folderKeys: KeyInfo[]
  documentKeys: KeyInfo[]
  onSetupKey?: () => void
  onRemoveKey?: (key: KeyInfo) => void
  onRotate?: (key: KeyInfo) => void
  onRevoke?: (key: KeyInfo) => void
  loading?: boolean
  error?: boolean
  totalKeyCount?: number
}

function MaskedArn({ arn }: { arn: string }) {
  const [copied, setCopied] = useState(false)
  const [showFull, setShowFull] = useState(false)
  const masked = arn ? `...${arn.slice(-12)}` : 'None'

  const handleCopy = () => {
    navigator.clipboard.writeText(arn)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs font-mono text-gray-500">
      <span onClick={() => setShowFull(!showFull)} className="cursor-pointer hover:text-gray-600">
        {showFull ? arn : masked}
      </span>
      {arn && (
        <button onClick={handleCopy} className="hover:text-gray-600" title="Copy key identifier">
          {copied ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
        </button>
      )}
    </span>
  )
}

function KeyNode({ keyInfo, level, label, isOverride, onRemove, onRotate, onRevoke }: {
  keyInfo?: KeyInfo
  level: number
  label: string
  isOverride: boolean
  onRemove?: (key: KeyInfo) => void
  onRotate?: (key: KeyInfo) => void
  onRevoke?: (key: KeyInfo) => void
}) {
  const [expanded, setExpanded] = useState(true)
  // Hover is tracked on the WHOLE node (header row + details panel): the panel
  // holds Rotate/Revoke and sits below the row, so closing it when the pointer
  // left the row made those buttons unreachable with a mouse (found by the
  // Story #6490 revoke journey). A click pins the panel open or shut.
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(false)
  const showDetails = hovered || pinned

  const indent = level * 24
  const isPlatform = keyInfo?.key_source === 'platform-managed'
  const isActive = keyInfo?.status === 'active'

  return (
    <div
      style={{ marginLeft: indent }}
      className="py-1.5"
      data-testid="key-node"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
          isOverride ? 'bg-iflow-50 border border-iflow-200' : 'bg-sky-500/5 border border-sky-200/30'
        }`}
        onClick={() => setPinned(!pinned)}
      >
        {/* Expand/collapse for parent nodes */}
        <button onClick={e => { e.stopPropagation(); setExpanded(!expanded) }} className="text-gray-500 hover:text-gray-600">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        {/* Icon */}
        {isOverride ? (
          <Shield size={14} className="text-iflow-500 flex-shrink-0" />
        ) : (
          <Key size={14} className="text-gray-500 flex-shrink-0" />
        )}

        {/* Label */}
        <span className={`text-sm font-medium ${isOverride ? 'text-iflow-700' : 'text-gray-600'}`}>
          {label}
        </span>

        {/* Key type badge */}
        {keyInfo && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
            isPlatform ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
          }`}>
            {isPlatform ? 'Platform' : 'Customer'}
          </span>
        )}

        {/* Status */}
        {keyInfo && (
          <span className={`text-[10px] ${isActive ? 'text-emerald-500' : 'text-red-400'}`}>
            {isActive ? '● Active' : '● Revoked'}
          </span>
        )}

        {/* Override indicator */}
        {isOverride && (
          <span className="text-[10px] bg-iflow-100 text-iflow-600 px-1.5 py-0.5 rounded-full font-medium ml-auto">
            Custom key
          </span>
        )}

        {!keyInfo && (
          <span className="text-[10px] text-gray-500 ml-auto">
            Inherited
          </span>
        )}
      </div>

      {/* Hover details */}
      {showDetails && keyInfo && (
        <div className="ml-8 mt-1 p-2 bg-white/80 backdrop-blur-sm border border-sky-200/50 rounded-lg shadow-sm text-xs space-y-1">
          <p className="text-gray-500">Key identifier: <MaskedArn arn={keyInfo.kms_key_arn} /></p>
          <p className="text-gray-500">Created: {new Date(keyInfo.created_at).toLocaleDateString()}</p>
          {keyInfo.rotated_at && <p className="text-gray-500">Last rotated: {new Date(keyInfo.rotated_at).toLocaleDateString()}</p>}
          {isOverride && onRemove && (
            <button onClick={() => onRemove(keyInfo)} className="text-red-500 hover:text-red-700 text-xs font-medium mt-1">
              Remove override (revert to parent key)
            </button>
          )}
          {isActive && (
            <div className="flex gap-2 mt-1.5 pt-1.5 border-t border-sky-200/30">
              {onRotate && (
                <button onClick={() => onRotate(keyInfo)} className="text-xs text-iflow-700 bg-iflow-50 border border-iflow-200 rounded px-2 py-1 hover:bg-iflow-100">
                  Rotate
                </button>
              )}
              {onRevoke && (
                <button onClick={() => onRevoke(keyInfo)} className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 hover:bg-red-100">
                  Revoke
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function KeyHierarchyTree({
  orgKeys, projectKeys, folderKeys, documentKeys, onSetupKey, onRemoveKey, onRotate, onRevoke, loading, error,
}: KeyHierarchyTreeProps) {
  if (loading) {
    return (
      <div className="space-y-3 py-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-4 animate-pulse">
            <div className="w-8 h-8 bg-sky-200 rounded-lg" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-sky-200 rounded w-1/3" />
              <div className="h-2 bg-sky-500/10 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8 px-4">
        <AlertTriangle size={32} className="mx-auto text-red-400 mb-3" />
        <h4 className="text-sm font-semibold text-gray-700 mb-1">Couldn&apos;t load your keys</h4>
        <p className="text-xs text-gray-500 mb-4 max-w-sm mx-auto">
          We hit a problem reaching the key service — use Refresh to try again.
        </p>
      </div>
    )
  }

  const hasAnyKey = orgKeys.length > 0 || projectKeys.length > 0 || folderKeys.length > 0 || documentKeys.length > 0

  if (!hasAnyKey) {
    return (
      <div className="text-center py-8 px-4">
        <Shield size={32} className="mx-auto text-gray-300 mb-3" />
        <h4 className="text-sm font-semibold text-gray-700 mb-1">No encryption keys yet</h4>
        <p className="text-xs text-gray-500 mb-4 max-w-sm mx-auto">
          Your data is protected by platform encryption. Set up a key to add an extra layer of security.
        </p>
        {onSetupKey && (
          <button onClick={onSetupKey} className="px-4 py-2 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 text-sm font-medium">
            Set Up Encryption Key
          </button>
        )}
      </div>
    )
  }

  const orgKey = orgKeys.find(k => k.status === 'active')

  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
        <Shield size={10} className="text-iflow-500" /> Bold = custom key set at this level
        <span className="mx-2">|</span>
        <Key size={10} className="text-gray-500" /> Muted = inherited from parent
      </p>

      {/* Organisation level (root) */}
      <KeyNode
        keyInfo={orgKey}
        level={0}
        label={orgKey ? 'Organisation' : 'Organisation (no key)'}
        isOverride={!!orgKey}
        onRemove={onRemoveKey}
        onRotate={onRotate}
        onRevoke={onRevoke}
      />

      {/* Project level */}
      {projectKeys.filter(k => k.status === 'active').map(pk => (
        <KeyNode
          key={pk.scope_id}
          keyInfo={pk}
          level={1}
          label={`Project: ${pk.scope_id}`}
          isOverride={true}
          onRemove={onRemoveKey}
          onRotate={onRotate}
          onRevoke={onRevoke}
        />
      ))}

      {/* Folder level */}
      {folderKeys.filter(k => k.status === 'active').map(fk => (
        <KeyNode
          key={fk.scope_id}
          keyInfo={fk}
          level={2}
          label={`Folder: ${fk.scope_id}`}
          isOverride={true}
          onRemove={onRemoveKey}
          onRotate={onRotate}
          onRevoke={onRevoke}
        />
      ))}

      {/* Document level */}
      {documentKeys.filter(k => k.status === 'active').map(dk => (
        <KeyNode
          key={dk.scope_id}
          keyInfo={dk}
          level={3}
          label={`Document: ${dk.scope_id}`}
          isOverride={true}
          onRemove={onRemoveKey}
          onRotate={onRotate}
          onRevoke={onRevoke}
        />
      ))}

      {projectKeys.length === 0 && folderKeys.length === 0 && documentKeys.length === 0 && orgKey && (
        <div className="ml-6 py-2 text-xs text-gray-500 italic">
          All projects, folders, and documents inherit the organisation key.
        </div>
      )}
    </div>
  )
}
